import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  IdCard,
  Loader2,
  MessageSquare,
  PauseCircle,
  Plus,
  ScrollText,
  ShieldCheck,
  Sparkles,
  UserCog,
  X,
} from 'lucide-react';
import { ChatMessageBubble, ChatInput, TypingIndicator, type ChatMessage } from '@/components/chat';
import { CoverageMap, coverageQueryKey } from '@/components/coverage/CoverageMap';
import { NextTopicsPanel } from '@/components/coverage/NextTopicsPanel';
import { nextTopicsQueryKey } from '@/services/coverage-v2';
import {
  TestUserPicker,
  TEST_USERS_QUERY_KEY,
  fetchTestUsers,
  type TestUser,
} from '@/components/interview/TestUserPicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { UserDetailDialog } from '@/components/users/UserDetailDialog';
import { apiClient } from '@/services/api-client';
import { useInterviewStream, type InterviewMetadata } from '@/hooks/useInterviewStream';
import { useStreamLock } from '@/hooks/useStreamLock';
import { useKickoffSse } from '@/hooks/useKickoffSse';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { SessionDebugStrip } from '@/components/interview/SessionDebugStrip';
import { useAuthStore } from '@/store/auth-store';
import { cn } from '@/lib/utils';
import type { SessionMessage, SessionSummary } from '@/types';

const AS_USER_PARAM = 'asUser';

/**
 * phase2-interfaces.md §2.1 — discriminated union for /interviews/start.
 * Phase 2 responses include `kickoffStatus`; legacy responses omit it. The
 * UI detects which flow it is on by `'kickoffStatus' in response`, not by
 * the value (which can be `null` on a clean resume).
 */
interface InterviewSession {
  sessionId: string;
  threadId: string;
  /** Present iff this is a Phase 2 response. */
  kickoffStatus?: 'pending' | 'ready' | null;
  /** Present iff this is a Phase 2 response. */
  resumed?: boolean;
}

// ── Query key helpers ──────────────────────────────────────────────────────
// Keyed on targetUserId so admin impersonation switches refetch cleanly
// instead of leaking the previous user's list/transcript.

const sessionsQueryKey = (targetUserId: string | null) =>
  ['interviews', 'sessions', targetUserId ?? 'self'] as const;

const transcriptQueryKey = (sessionId: string, targetUserId: string | null) =>
  ['interviews', 'sessions', targetUserId ?? 'self', sessionId, 'messages'] as const;

// ── Date / duration helpers ────────────────────────────────────────────────

function groupSessionsByDate(
  sessions: SessionSummary[]
): { label: string; items: SessionSummary[] }[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 6 * 86400000);

  const groups: Record<string, SessionSummary[]> = {};
  const order: string[] = [];

  for (const session of sessions) {
    const d = new Date(session.startedAt);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let label: string;
    if (day.getTime() === today.getTime()) {
      label = 'Today';
    } else if (day.getTime() === yesterday.getTime()) {
      label = 'Yesterday';
    } else if (day >= weekAgo) {
      label = day.toLocaleDateString(undefined, { weekday: 'long' });
    } else {
      label = day.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    if (!(label in groups)) order.push(label);
    (groups[label] ??= []).push(session);
  }

  return order.map((label) => ({ label, items: groups[label] }));
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ── Session history sidebar ────────────────────────────────────────────────

interface SessionHistoryPanelProps {
  targetUserId: string | null;
  selectedSessionId: string | null;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

function SessionHistoryPanel({
  targetUserId,
  selectedSessionId,
  onSelectSession,
  onNewSession,
  isOpen,
  onToggle,
}: SessionHistoryPanelProps) {
  const { data, isLoading } = useQuery<SessionSummary[]>({
    queryKey: sessionsQueryKey(targetUserId),
    queryFn: async () => {
      const params: Record<string, string> = { limit: '50' };
      if (targetUserId) params.targetUserId = targetUserId;
      const res = await apiClient.get<SessionSummary[]>('/api/v1/interviews/sessions', params);
      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load sessions');
      }
      return (res.data as unknown as SessionSummary[]) ?? [];
    },
    staleTime: 30_000,
  });

  const groups = data ? groupSessionsByDate(data) : [];

  return (
    <aside
      aria-label="Session history"
      className={cn(
        'relative flex shrink-0 flex-col border-r border-border bg-card transition-all duration-200',
        isOpen ? 'w-64' : 'w-10'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={isOpen ? 'Collapse history' : 'Expand history'}
        className="absolute -right-3 top-4 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background shadow-sm hover:bg-accent"
      >
        {isOpen ? <ChevronLeft className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>

      {isOpen ? (
        <>
          <div className="flex items-center justify-between border-b border-border px-3 py-3">
            <span className="font-display text-sm font-semibold text-foreground">
              Session History
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={onNewSession}
              aria-label="New session"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto py-2">
            {isLoading ? (
              <div className="space-y-2 px-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-md" />
                ))}
              </div>
            ) : groups.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-3 py-8 text-center">
                <ScrollText className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-xs text-muted-foreground">No sessions yet</p>
              </div>
            ) : (
              groups.map(({ label, items }) => (
                <div key={label} className="mb-3">
                  <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {label}
                  </p>
                  {items.map((session) => (
                    <button
                      key={session.id}
                      type="button"
                      onClick={() => onSelectSession(session.id)}
                      className={cn(
                        'flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-accent',
                        selectedSessionId === session.id && 'bg-accent'
                      )}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="truncate text-xs font-medium text-foreground">
                          {formatTime(session.startedAt)}
                        </span>
                        <Badge
                          variant={session.status === 'completed' ? 'secondary' : 'outline'}
                          className="shrink-0 px-1 py-0 text-[9px]"
                        >
                          {session.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-0.5">
                          <MessageSquare className="h-2.5 w-2.5" />
                          {session.messageCount}
                        </span>
                        {session.durationSeconds != null && (
                          <span className="flex items-center gap-0.5">
                            <Clock className="h-2.5 w-2.5" />
                            {formatDuration(session.durationSeconds)}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground"
          aria-label="Expand history"
        >
          <ScrollText className="h-4 w-4" />
        </button>
      )}
    </aside>
  );
}

// ── Read-only transcript view ──────────────────────────────────────────────

interface TranscriptViewProps {
  sessionId: string;
  targetUserId: string | null;
  onBack: () => void;
}

function TranscriptView({ sessionId, targetUserId, onBack }: TranscriptViewProps) {
  const {
    data: messages,
    isLoading,
    error,
  } = useQuery<SessionMessage[], Error>({
    queryKey: transcriptQueryKey(sessionId, targetUserId),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (targetUserId) params.targetUserId = targetUserId;
      const res = await apiClient.get<SessionMessage[]>(
        `/api/v1/interviews/sessions/${sessionId}/messages`,
        Object.keys(params).length ? params : undefined
      );
      if (!res.success) {
        throw new Error(res.error?.message ?? 'Failed to load transcript');
      }
      return (res.data as unknown as SessionMessage[]) ?? [];
    },
  });

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [messages]);

  return (
    <section aria-label="Past session transcript" className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-2">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Past session transcript</span>
          <Badge variant="secondary" className="text-[10px]">
            Read-only
          </Badge>
        </div>
        <Button size="sm" variant="outline" onClick={onBack}>
          <X className="mr-1 h-3.5 w-3.5" />
          Back to live session
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                className={cn('h-14 rounded-lg', i % 2 === 0 ? 'ml-auto w-2/3' : 'w-2/3')}
              />
            ))}
          </div>
        ) : error ? (
          <div
            role="alert"
            className="mt-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error.message}
          </div>
        ) : !messages || messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <ScrollText className="h-10 w-10 text-muted-foreground/40" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">No transcript available</p>
              <p className="mt-1 text-xs text-muted-foreground/70">
                This session predates transcript recording.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => (
              <ChatMessageBubble
                key={msg.id}
                message={{
                  id: msg.id,
                  role: msg.role === 'assistant' ? 'assistant' : 'user',
                  content: msg.content,
                  timestamp: new Date(msg.createdAt),
                  kind: msg.kind ?? null,
                }}
              />
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>
    </section>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export function Interview() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth for the impersonation target so refresh keeps
  // the admin on the same test user. A stale id (user deleted, no longer
  // @yopmail) will simply fail the next server call with 403.
  const targetUserId = searchParams.get(AS_USER_PARAM);

  const { data: testUsers } = useQuery<TestUser[]>({
    queryKey: TEST_USERS_QUERY_KEY,
    queryFn: fetchTestUsers,
    staleTime: 60_000,
  });
  const targetUser = useMemo(
    () => testUsers?.find((u) => u.id === targetUserId) ?? null,
    [testUsers, targetUserId]
  );

  const [session, setSession] = useState<InterviewSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [historyOpen, setHistoryOpen] = useState(true);
  const [viewingSessionId, setViewingSessionId] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);

  // Brief 3 §8.3 — debug strip is rendered only for super_admins. We read
  // the flag once into a ref-stable boolean rather than re-evaluating on
  // every render so the strip's mount/unmount stays predictable for tests.
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin)();

  const streamingIdRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── Phase 2 wiring (kickoff SSE + stream-lock) ─────────────────────────
  // Whether the *current* session is Phase 2 is determined by the response
  // shape we held onto (presence of `kickoffStatus`), not by the boot-time
  // flag. The flag still drives the UI's *next* `/start` decision via the
  // server's response — we don't need to read it ourselves here.
  useFeatureFlags();
  const isPhase2Session = session ? 'kickoffStatus' in session : false;
  const shouldSubscribeKickoff =
    isPhase2Session &&
    !!session?.sessionId &&
    !session?.resumed &&
    session.kickoffStatus === 'pending';

  const lock = useStreamLock({
    sessionId: session?.sessionId ?? null,
    targetUserId,
    enabled: isPhase2Session,
  });

  // Coverage data is fetched + rendered by <CoverageMap /> directly; the
  // page-level priority-topics derivation that used to live here is
  // replaced by the server-driven <NextTopicsPanel /> in the right rail.

  const handleToken = useCallback((content: string) => {
    const id = streamingIdRef.current;
    if (!id) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, content: m.content + content } : m))
    );
  }, []);

  const handleDone = useCallback(() => {
    streamingIdRef.current = null;
    // Refresh history once the turn completes — the session (created lazily
    // on first send) should now surface in the sidebar with an updated
    // messageCount.
    queryClient.invalidateQueries({ queryKey: sessionsQueryKey(targetUserId) });
  }, [queryClient, targetUserId]);

  const handleLockLost = useCallback(() => {
    // The stream-lock hook will already flip its own state to `'lost'` via
    // the heartbeat 409, but the message-send path may detect the loss
    // first. Cancel the in-flight stream so the user isn't left staring at
    // a half-emitted bubble.
    streamingIdRef.current = null;
  }, []);

  const stream = useInterviewStream({
    sessionId: session?.sessionId ?? null,
    threadId: session?.threadId ?? null,
    targetUserId,
    lockToken: lock.lockToken,
    onLockLost: handleLockLost,
    onToken: handleToken,
    onDone: handleDone,
  });

  // ── Kickoff SSE — Phase 2 only, gated on session.kickoffStatus ─────────
  const handleKickoffEvent = useCallback(
    (
      event:
        | { kind: 'turn'; text: string; createdAt: string }
        | { kind: 'error'; code: string; message: string }
    ) => {
      if (event.kind === 'turn') {
        const kickoff: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: event.text,
          timestamp: new Date(event.createdAt),
          kind: 'kickoff',
        };
        setMessages((prev) => [...prev, kickoff]);
      } else {
        setStartError(event.message);
      }
    },
    []
  );

  useKickoffSse({
    sessionId: session?.sessionId ?? null,
    targetUserId,
    enabled: shouldSubscribeKickoff,
    onEvent: handleKickoffEvent,
  });

  // ── Best-effort lock release on tab close (Phase 2 §1.5.4) ────────────
  useEffect(() => {
    if (!isPhase2Session || !session?.sessionId) return;
    const handler = () => {
      // sendBeacon is fire-and-forget and survives a closing tab; the
      // server endpoint accepts the same JSON body the in-page client uses.
      const payload = new Blob([JSON.stringify(targetUserId ? { targetUserId } : {})], {
        type: 'application/json',
      });
      navigator.sendBeacon(`/api/v1/interviews/${session.sessionId}/pause`, payload);
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [isPhase2Session, session?.sessionId, targetUserId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, stream.isStreaming]);

  // When the admin switches test users (or clears), drop the live session +
  // any open transcript, and invalidate the per-user queries so the sidebar
  // and coverage map refetch under the new scope. We never call /end here:
  // the in-flight session belongs to the prior user's scope and the follow-up
  // "close on switch" behavior is out of scope for this PR.
  const cancelRef = useRef(stream.cancel);
  const qcRef = useRef(queryClient);
  useEffect(() => {
    cancelRef.current = stream.cancel;
    qcRef.current = queryClient;
  });
  useEffect(() => {
    cancelRef.current();
    setSession(null);
    setMessages([]);
    streamingIdRef.current = null;
    setStartError(null);
    setViewingSessionId(null);
    qcRef.current.invalidateQueries({ queryKey: ['interviews', 'coverage'] });
    qcRef.current.invalidateQueries({ queryKey: ['interviews', 'sessions'] });
  }, [targetUserId]);

  function handleTargetSelect(user: TestUser | null) {
    const next = new URLSearchParams(searchParams);
    if (user) {
      next.set(AS_USER_PARAM, user.id);
    } else {
      next.delete(AS_USER_PARAM);
    }
    setSearchParams(next, { replace: true });
  }

  // Start (or resume) a session. Phase 2 returns a richer shape with
  // `kickoffStatus` + `resumed`; legacy returns just `{ sessionId, threadId }`.
  // The presence of `kickoffStatus` in the response is the discriminator
  // (see InterviewSession typedef and phase2-interfaces.md §2.1).
  const ensureSession = useCallback(async (): Promise<InterviewSession | null> => {
    if (session) return session;
    setIsStarting(true);
    setStartError(null);
    const response = await apiClient.post<InterviewSession>(
      '/api/v1/interviews/start',
      targetUserId ? { targetUserId } : undefined
    );
    setIsStarting(false);
    if (!response.success || !response.data) {
      setStartError(response.error?.message ?? 'Could not start a session');
      return null;
    }
    const next = response.data as unknown as InterviewSession;
    setSession(next);

    // For a clean Phase 2 resume, hydrate the transcript from the server —
    // the kickoff already played in a prior session-page load. For a fresh
    // Phase 2 start, leave messages empty: `useKickoffSse` will append the
    // kickoff bubble as soon as the worker publishes. Legacy responses
    // never set kickoffStatus and follow the existing zero-message path.
    if ('kickoffStatus' in next && next.resumed) {
      try {
        const params: Record<string, string> = {};
        if (targetUserId) params.targetUserId = targetUserId;
        const transcript = await apiClient.get<SessionMessage[]>(
          `/api/v1/interviews/sessions/${next.sessionId}/messages`,
          Object.keys(params).length ? params : undefined
        );
        if (transcript.success && transcript.data) {
          const data = transcript.data as unknown as SessionMessage[];
          setMessages(
            data.map((m) => ({
              id: m.id,
              role: m.role === 'assistant' ? 'assistant' : 'user',
              content: m.content,
              timestamp: new Date(m.createdAt),
              kind: m.kind ?? null,
            }))
          );
        } else {
          setMessages([]);
        }
      } catch {
        setMessages([]);
      }
    } else {
      setMessages([]);
    }
    return next;
  }, [session, targetUserId]);

  async function handleStartExplicit() {
    setViewingSessionId(null);
    const started = await ensureSession();
    if (!started) return;
  }

  async function handleEnd() {
    if (!session) return;
    setIsEnding(true);
    stream.cancel();
    lock.release();
    await apiClient.post('/api/v1/interviews/end', {
      sessionId: session.sessionId,
      threadId: session.threadId,
      ...(targetUserId ? { targetUserId } : {}),
    });
    setIsEnding(false);
    setSession(null);
    setMessages([]);
    streamingIdRef.current = null;
    queryClient.invalidateQueries({ queryKey: coverageQueryKey(targetUserId) });
    queryClient.invalidateQueries({ queryKey: nextTopicsQueryKey(targetUserId) });
    queryClient.invalidateQueries({ queryKey: sessionsQueryKey(targetUserId) });
  }

  async function handlePause() {
    if (!session || !isPhase2Session) return;
    setIsPausing(true);
    stream.cancel();
    lock.release();
    await apiClient.post(
      `/api/v1/interviews/${session.sessionId}/pause`,
      targetUserId ? { targetUserId } : undefined
    );
    setIsPausing(false);
    setSession(null);
    setMessages([]);
    streamingIdRef.current = null;
    queryClient.invalidateQueries({ queryKey: sessionsQueryKey(targetUserId) });
  }

  function handleNewSession() {
    stream.cancel();
    setSession(null);
    setMessages([]);
    streamingIdRef.current = null;
    setStartError(null);
    setViewingSessionId(null);
  }

  async function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || stream.isStreaming) return;

    const active = await ensureSession();
    if (!active) return;

    // Phase 2: acquire the stream-lock before sending. If we already hold
    // it, this is a no-op; if contended, the takeover banner renders and
    // the user must explicitly take over before the message goes through.
    //
    // We thread the lock token through `acquire()`'s synchronous return
    // value (not React state) and pass it to `stream.send` as an explicit
    // override — the React render cycle that propagates `lock.lockToken`
    // into `useInterviewStream`'s closure has not happened yet at the
    // moment we call send(), so reading from state here would dispatch a
    // POST with no `X-Stream-Lock` header and the API would 400. See
    // `useStreamLock.AcquireResult` and `useInterviewStream.send`'s
    // `lockTokenOverride` arg.
    const isPhase2 = 'kickoffStatus' in active;
    let sendLockToken: string | null | undefined;
    if (isPhase2) {
      // If we already hold a valid token, reuse it (sync-safe: we're in
      // the 'held' branch precisely because state.status === 'held').
      // Otherwise (re-)acquire and read the fresh token from acquire()'s
      // synchronous return — NOT from `lock.state` / `lock.lockToken`,
      // which won't reflect the new value until React re-renders.
      const acquireResult =
        lock.state.status === 'held'
          ? { status: 'held' as const, lockToken: lock.state.lockToken }
          : await lock.acquire();
      if (acquireResult.status === 'contended') {
        // Banner is already rendered; bail and let the user take over
        // explicitly. handleSend will re-run on their next click.
        return;
      }
      if (acquireResult.status !== 'held') {
        // 'lost' | 'error' — surface via the existing error UI on next
        // render and stop. Don't dispatch a request that's certain to 400.
        return;
      }
      sendLockToken = acquireResult.lockToken;
    }

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };
    const assistantPlaceholder: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    streamingIdRef.current = assistantPlaceholder.id;
    setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
    setInput('');
    void stream.send(trimmed, sendLockToken);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape' && stream.isStreaming) {
      e.preventDefault();
      stream.cancel();
    }
  }

  const lastAssistantIsStreaming =
    stream.isStreaming && messages.length > 0 && messages[messages.length - 1].role === 'assistant';
  const waitingForFirstToken =
    lastAssistantIsStreaming && messages[messages.length - 1].content === '';

  return (
    <div className="flex h-[calc(100vh-5rem)] flex-col overflow-hidden" onKeyDown={handleKeyDown}>
      <div className="shrink-0 pb-3">
        <ImpersonationRow
          targetUserId={targetUserId}
          targetUser={targetUser}
          onSelect={handleTargetSelect}
          onViewProfile={() => setProfileOpen(true)}
        />
      </div>

      <UserDetailDialog userId={targetUserId} open={profileOpen} onOpenChange={setProfileOpen} />

      <div className="flex shrink-0 items-center justify-between border-b pb-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Life-Story Interview</h1>
          <p className="text-muted-foreground">
            A quiet conversation that becomes a life told in their own words.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {session && session.resumed ? (
            <Badge variant="outline" className="border-orb-teal/40 text-orb-teal">
              Continued
            </Badge>
          ) : null}
          {session ? (
            <>
              {isPhase2Session && (
                <Button
                  variant="outline"
                  onClick={handlePause}
                  disabled={isPausing || isEnding}
                  className="border-orb-teal/40 text-orb-teal hover:bg-orb-teal/10"
                >
                  {isPausing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <PauseCircle className="mr-2 h-4 w-4" />
                  )}
                  Pause
                </Button>
              )}
              <Button
                onClick={handleEnd}
                disabled={isEnding || isPausing}
                className="bg-orb-teal text-white hover:bg-orb-teal/90"
              >
                {isEnding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                End session
              </Button>
            </>
          ) : (
            <Button
              onClick={handleStartExplicit}
              disabled={isStarting}
              className="bg-orb-gold text-white hover:bg-orb-gold/90"
            >
              {isStarting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Start session
            </Button>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-4 pt-4">
        <SessionHistoryPanel
          targetUserId={targetUserId}
          selectedSessionId={viewingSessionId}
          onSelectSession={(id) => setViewingSessionId(id)}
          onNewSession={handleNewSession}
          isOpen={historyOpen}
          onToggle={() => setHistoryOpen((v) => !v)}
        />

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 overflow-hidden md:grid-cols-[1fr_20rem]">
          {/* Main column — live chat or read-only transcript */}
          {viewingSessionId ? (
            <TranscriptView
              key={viewingSessionId}
              sessionId={viewingSessionId}
              targetUserId={targetUserId}
              onBack={() => setViewingSessionId(null)}
            />
          ) : (
            <section aria-label="Interview chat" className="flex min-h-0 flex-col">
              {/* Brief 3 §8.3 — admin-only debug strip. Rendered above
                  the lock banner so the admin can see the debug data
                  even when a takeover prompt is up. */}
              {isSuperAdmin && isPhase2Session && session?.sessionId && (
                <SessionDebugStrip
                  sessionId={session.sessionId}
                  resumed={!!session.resumed}
                  kickoffStatus={session.kickoffStatus ?? null}
                />
              )}
              {/* Phase 2 stream-lock banner (§1.5.4 / 8.1a). Non-modal: the
                  user keeps full control of the input and can take over,
                  dismiss, or do nothing. */}
              {lock.state.status === 'contended' && (
                <div
                  role="alert"
                  className="mb-2 flex items-start justify-between gap-3 rounded-md border border-orb-gold/40 bg-orb-cream/60 px-3 py-2 text-sm text-foreground"
                >
                  <span>
                    This conversation is open in another window
                    {lock.state.heldSince
                      ? ` since ${new Date(lock.state.heldSince).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      : ''}
                    .
                  </span>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="ghost" onClick={() => lock.release()}>
                      Close
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void lock.takeover()}
                      className="bg-orb-gold text-white hover:bg-orb-gold/90"
                    >
                      Take over here
                    </Button>
                  </div>
                </div>
              )}
              {lock.state.status === 'lost' && (
                <div
                  role="alert"
                  className="mb-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                  data-testid="lock-lost-notice"
                >
                  Opened in another window. This window is no longer active.
                </div>
              )}
              <div className="flex-1 overflow-y-auto pr-1">
                {!session ? (
                  <Phase2EmptyState
                    isStarting={isStarting}
                    error={startError}
                    onStart={handleStartExplicit}
                  />
                ) : messages.length === 0 && !shouldSubscribeKickoff ? (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <div className="rounded-full bg-orb-cream p-4">
                      <Sparkles className="h-8 w-8 text-orb-gold" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold">Ready when you are</h3>
                    <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                      Start with whatever feels natural — a memory, a moment, a place.
                    </p>
                  </div>
                ) : messages.length === 0 && shouldSubscribeKickoff ? (
                  <div
                    className="flex h-full flex-col items-center justify-center text-center"
                    data-testid="kickoff-waiting"
                  >
                    <TypingIndicator />
                    <p className="mt-3 text-sm text-muted-foreground">composing…</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message, idx) => {
                      const isLast = idx === messages.length - 1;
                      const isStreamingBubble =
                        isLast && lastAssistantIsStreaming && message.role === 'assistant';
                      return (
                        <div
                          key={message.id}
                          aria-live={isStreamingBubble ? 'polite' : undefined}
                          aria-atomic={isStreamingBubble ? false : undefined}
                        >
                          <ChatMessageBubble message={message} />
                        </div>
                      );
                    })}
                    {waitingForFirstToken && <TypingIndicator />}
                    {stream.lastMetadata && <MetadataChip metadata={stream.lastMetadata} />}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {stream.error && (
                <div
                  role="alert"
                  className="mt-2 flex items-start justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                >
                  <span>Something interrupted the stream: {stream.error.message}</span>
                </div>
              )}

              <div className="mt-4 border-t pt-4">
                <ChatInput
                  value={input}
                  onChange={setInput}
                  onSend={handleSend}
                  disabled={stream.isStreaming || isStarting}
                  placeholder={
                    stream.isStreaming
                      ? 'Listening…'
                      : session
                        ? 'Share a memory…'
                        : 'Share a memory to begin…'
                  }
                />
                <p className="mt-2 text-center text-[11px] text-muted-foreground">
                  Press Enter to send · Shift+Enter for a new line · Esc to cancel a response
                </p>
              </div>
            </section>
          )}

          {/* Right sidebar — coverage + next topics (always visible) */}
          <aside
            aria-label="Life coverage"
            className="flex min-h-0 flex-col gap-4 overflow-y-auto pb-2"
          >
            <CoverageMap targetUserId={targetUserId} />
            <NextTopicsPanel targetUserId={targetUserId} />
          </aside>
        </div>
      </div>
    </div>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────────────

/**
 * Single-row admin impersonation control: picker, acting-as indicator,
 * and "View profile" CTA live together so the admin sees their full
 * test context at a glance. When no user is selected, only the picker
 * renders — the row stays visually calm for the common "as yourself"
 * case.
 */
function ImpersonationRow({
  targetUserId,
  targetUser,
  onSelect,
  onViewProfile,
}: {
  targetUserId: string | null;
  targetUser: TestUser | null;
  onSelect: (user: TestUser | null) => void;
  onViewProfile: () => void;
}) {
  const actingName = targetUser
    ? [targetUser.firstName, targetUser.lastName].filter(Boolean).join(' ') || targetUser.email
    : null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border px-4 py-2 transition-colors',
        targetUserId ? 'border-orb-teal/40 bg-orb-teal/10' : 'border-border bg-muted/30'
      )}
    >
      <TestUserPicker selectedUserId={targetUserId} onSelect={onSelect} />

      {targetUserId && (
        <>
          <span
            role="status"
            aria-live="polite"
            className="flex min-w-0 items-center gap-2 text-sm text-orb-teal"
          >
            <UserCog className="h-4 w-4 shrink-0" />
            {actingName ? (
              <span className="truncate">
                Acting as <strong className="font-semibold">{actingName}</strong>
                {targetUser?.personaName ? (
                  <span className="text-orb-teal/80"> — {targetUser.personaName}</span>
                ) : null}
              </span>
            ) : (
              <span className="text-orb-teal/80">Loading test user…</span>
            )}
          </span>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onViewProfile}
            className="ml-auto border-orb-teal/40 bg-white text-orb-teal hover:bg-orb-teal/10 hover:text-orb-teal"
          >
            <IdCard className="mr-1.5 h-3.5 w-3.5" />
            View user profile
          </Button>
        </>
      )}
    </div>
  );
}

/**
 * Phase 2 empty state (§8.1) — replaces the old "Begin a quiet
 * conversation" copy. The brief calls for a single primary CTA when no
 * active session exists; the agent's kickoff turn carries the opening
 * line, so the EmptyState no longer needs to set the conversational tone.
 */
function Phase2EmptyState({
  onStart,
  isStarting,
  error,
}: {
  onStart: () => void;
  isStarting: boolean;
  error: string | null;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="max-w-md">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orb-cream">
          <Sparkles className="h-7 w-7 text-orb-gold" />
        </div>
        <Button
          data-testid="start-session-button"
          onClick={onStart}
          disabled={isStarting}
          size="lg"
          className="mt-6 bg-orb-gold text-white hover:bg-orb-gold/90"
        >
          {isStarting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Start session
        </Button>
        {error && (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function MetadataChip({ metadata }: { metadata: InterviewMetadata }) {
  const tone = metadata.emotion?.emotionalTone?.trim() || null;
  if (!tone && !metadata.piiDetected) return null;
  return (
    <div
      className={cn('mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground')}
      aria-label="Response metadata"
    >
      {tone && (
        <span className="inline-flex items-center gap-1 rounded-full border border-orb-gold/40 bg-orb-cream px-2 py-0.5 font-display text-foreground">
          Tone · {tone}
        </span>
      )}
      {metadata.piiDetected && (
        <span className="inline-flex items-center gap-1 rounded-full border border-orb-teal/40 bg-orb-teal/10 px-2 py-0.5 text-orb-teal">
          <ShieldCheck className="h-3 w-3" /> PII redacted
        </span>
      )}
    </div>
  );
}
